// v10/src/components/admin/lifespan/LifespanTab.js
//
// Pestaña "Vida límite por aeronave" del módulo Mantenimiento e inventario (A6.1).
// Container con toggle entre "Por aeronave" y "Global".
//
// En T2 se agrega state lifted para coordinar la navegación: cuando el usuario
// hace click en una fila de GlobalAttentionTable, se cambia a "Por aeronave"
// y se pasan `initialAircraftId` + `initialComponentId` para preseleccionar
// la aeronave y hacer scroll/highlight a la card del componente.

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plane, LayoutGrid, FileDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import ByAircraftView from './ByAircraftView';
import GlobalView from './GlobalView';
import LifespanDashboardWidget from './LifespanDashboardWidget';
import ExportReportModal from '../../common/ExportReportModal';
import reportsService from '../../../services/reportsService';
import useIsMobile from '../../../hooks/useIsMobile';

const VIEW_BY_AIRCRAFT = 'by_aircraft';
const VIEW_GLOBAL      = 'global';

const EXPORT_ROLES = new Set(['admin', 'instructor', 'superadmin']);

const LifespanTab = ({ darkMode, userRole, locale = 'es-AR', isActive = true }) => {
  const { t } = useTranslation('admin');
  const isMobile = useIsMobile();
  const [view, setView] = useState(VIEW_BY_AIRCRAFT);
  const [exportOpen, setExportOpen] = useState(false);

  // Estado lifted para navegación Global → Por aeronave
  const [initialAircraftId, setInitialAircraftId] = useState(null);
  const [initialComponentId, setInitialComponentId] = useState(null);

  const canExport = EXPORT_ROLES.has(userRole);

  const fgMuted = darkMode ? 'rgba(255,255,255,0.55)' : '#64748b';

  const VIEWS = [
    { id: VIEW_BY_AIRCRAFT, label: t('lifespan_tab_view_by_aircraft'), icon: Plane },
    { id: VIEW_GLOBAL,      label: t('lifespan_tab_view_global'),      icon: LayoutGrid },
  ];

  const handleSelectFromGlobal = ({ aircraftId, componentId }) => {
    setInitialAircraftId(aircraftId || null);
    setInitialComponentId(componentId || null);
    setView(VIEW_BY_AIRCRAFT);
  };

  // Cuando ByAircraftView ya aplicó el initialComponentId (scroll/highlight) lo limpia,
  // así un cambio manual de aeronave no vuelve a disparar el highlight.
  const handleInitialApplied = () => {
    setInitialComponentId(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* Widget de resumen (A6.2 parte 2B) */}
      <LifespanDashboardWidget darkMode={darkMode} />

      {/* Toggle pills + botón Exportar */}
      <div style={isMobile ? {
        display: 'flex', flexDirection: 'column', gap: 10,
      } : {
        display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center',
        justifyContent: 'space-between',
      }}>
      <div
        role="tablist"
        aria-label={t('lifespan_tab_view_label')}
        style={isMobile ? {
          display: 'flex', flexWrap: 'nowrap',
          overflowX: 'auto', WebkitOverflowScrolling: 'touch',
          padding: 4, borderRadius: 14, gap: 4,
          background: darkMode ? 'rgba(255,255,255,0.05)' : '#f1f5f9',
          border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e2e8f0',
          alignSelf: 'stretch',
        } : {
          display: 'inline-flex',
          padding: 4, borderRadius: 14, gap: 4,
          background: darkMode ? 'rgba(255,255,255,0.05)' : '#f1f5f9',
          border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e2e8f0',
        }}
      >
        {VIEWS.map((v) => {
          const isSelected = v.id === view;
          const Icon = v.icon;
          return (
            <button
              key={v.id}
              type="button"
              role="tab"
              aria-selected={isSelected}
              onClick={() => setView(v.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 14px', borderRadius: 11, border: 'none', cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                background: isSelected
                  ? (darkMode ? 'rgba(255,255,255,0.10)' : 'white')
                  : 'transparent',
                color: isSelected
                  ? (darkMode ? 'white' : '#0f172a')
                  : fgMuted,
                boxShadow: isSelected ? '0 2px 8px rgba(15,23,42,0.08)' : 'none',
                transition: 'all 0.18s',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                ...(isMobile ? { flex: 1, justifyContent: 'center' } : null),
              }}
            >
              <Icon style={{ width: 14, height: 14 }} />
              {v.label}
            </button>
          );
        })}
      </div>

      {canExport && (
        <motion.button
          type="button"
          onClick={() => setExportOpen(true)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.97 }}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '10px 16px', borderRadius: 12, cursor: 'pointer',
            background: darkMode ? 'rgba(14,165,233,0.18)' : 'rgba(14,165,233,0.1)',
            color: '#0ea5e9', fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
            border: '1px solid rgba(14,165,233,0.35)',
            ...(isMobile ? { justifyContent: 'center', width: '100%' } : null),
          }}
        >
          <FileDown style={{ width: 14, height: 14 }} />
          {t('report_button_export')}
        </motion.button>
      )}
      </div>

      {/* Contenido del view activo */}
      <AnimatePresence mode="wait">
        <motion.div
          key={view}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.16 }}
        >
          {view === VIEW_BY_AIRCRAFT && (
            <ByAircraftView
              darkMode={darkMode}
              userRole={userRole}
              locale={locale}
              isActive={isActive && view === VIEW_BY_AIRCRAFT}
              initialAircraftId={initialAircraftId}
              initialComponentId={initialComponentId}
              onInitialApplied={handleInitialApplied}
            />
          )}
          {view === VIEW_GLOBAL && (
            <GlobalView
              darkMode={darkMode}
              isActive={isActive && view === VIEW_GLOBAL}
              locale={locale}
              onSelect={handleSelectFromGlobal}
            />
          )}
        </motion.div>
      </AnimatePresence>

      <AnimatePresence>
        {exportOpen && (
          <ExportReportModal
            isOpen={exportOpen}
            title={t('report_modal_title_maintenance')}
            formats={['pdf', 'xlsx']}
            periodOptions={{ monthPicker: true, rangePicker: true }}
            onExport={(params) => reportsService.maintenance(params)}
            onClose={() => setExportOpen(false)}
            darkMode={darkMode}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default LifespanTab;
